/**
 * WHETHER THE TO-DO LIST SHOWS ITS MIC AND ITS "AI HELP" - the one place on
 * the screen that decides.
 *
 * Every mic button (quick add, the chat's Make a to-do) and every AI button
 * (Break into steps, Checklist from text, in the detail panel and the ⋯
 * menu) asks here, and nowhere else.
 *
 * WHAT IT CHECKS TODAY (22 October 2026):
 *
 *   voiceAllowed(features)  GET /todos/features said `voice: true` - the
 *                           server's lib/todo-assist-gate.js: To do access as
 *                           today, and Azure Speech is set up - AND this
 *                           browser can record and convert (getUserMedia,
 *                           MediaRecorder, an AudioContext to make the WAV).
 *   aiAllowed(features)     GET /todos/features said `ai_assist: true` - the
 *                           same gate: To do access, and ANTHROPIC_API_KEY.
 *
 * Anything missing - an older server, a failed features call - is a no:
 * nothing is drawn rather than a button that then fails.
 *
 * WHERE THE FEATURE SWITCHES GO. The admin feature switches (another branch:
 * a frontend `useFeature()`) join in here, and only here:
 *
 *   voice <- useFeature('Recording & transcription') && useFeature('To do')
 *   AI    <- useFeature('AI summaries, minutes & to-dos') && useFeature('To do')
 *
 * The server checks the same switches on every call, so the screen hiding a
 * button is a courtesy, never the lock.
 */

/** Whether this browser can record a few seconds and turn them into a 16 kHz WAV. */
export function canDictate() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const media = navigator.mediaDevices;
  return Boolean(media && media.getUserMedia)
    && Boolean(window.MediaRecorder)
    && Boolean(window.AudioContext || window.webkitAudioContext)
    && Boolean(window.OfflineAudioContext || window.webkitOfflineAudioContext);
}

export const voiceAllowed = (features) => features?.voice === true && canDictate();
export const aiAllowed = (features) => features?.ai_assist === true;

/**
 * The languages a dictation may be in, as the server says - or Auto and the
 * five it always has. Auto (Malti + English) is the default and needs AI
 * help: the server recognises the recording as both and Claude reads the two
 * together (23 October).
 */
export const DEFAULT_LANGUAGES = [
  { code: 'auto', name: 'Auto (Malti + English)', short: 'AUTO', needs_ai: true },
  { code: 'en-GB', name: 'English', short: 'EN' },
  { code: 'mt-MT', name: 'Malti', short: 'MT' },
  { code: 'it-IT', name: 'Italiano', short: 'IT' },
  { code: 'de-DE', name: 'Deutsch', short: 'DE' },
  { code: 'fr-FR', name: 'Français', short: 'FR' }
];

/** The batch C part of a /todos/features answer, read defensively. */
export function readAssistFeatures(data) {
  const langs = Array.isArray(data?.voice_languages)
    ? data.voice_languages.filter((l) => l && typeof l.code === 'string' && typeof l.name === 'string')
      .map((l) => ({ code: l.code, name: l.name, short: l.short, ...(l.needs_ai === true ? { needs_ai: true } : {}) })) : [];
  return {
    voice: data?.voice === true,
    ai_assist: data?.ai_assist === true,
    voice_languages: langs.length ? langs : DEFAULT_LANGUAGES,
    voice_max_seconds: Number.isInteger(data?.voice_max_seconds) && data.voice_max_seconds > 0 ? data.voice_max_seconds : 30
  };
}

/**
 * The languages as the chip offers them: one that needs AI is DISABLED, with
 * a hint, when AI help is off for this person (`unavailable`).
 */
export const languagesFor = (features) => (features?.voice_languages || DEFAULT_LANGUAGES).map((l) => (l.needs_ai && features?.ai_assist !== true
  ? { ...l, unavailable: 'Needs AI help, which is off for you - pick the language you speak' } : l));
